// Synthetic transcript corpus for the conversation-summaries suites.
//
// EVERY transcript here is invented. No real member data may ever be added to
// this file — the corpus is the regression bed the pipeline is proven against,
// and it must stay shippable in a public diff (ADR-075 Rule 8 discipline
// applies to fixtures as much as to the memory store).
//
// Seven scenarios, one per behaviour the spec pins: short-trivial (substantive
// false), long multi-topic, crisis-content, tool-call-heavy (tool traffic
// never reaches the model), commitment-rich (grounded member-turn citations),
// unattributed (a row property — see `unattributedConversation`), and
// Sona-named (the agent introduces itself by name while the member talks about
// a human coach, so the summary must name Sona and give her no role word).

import { canonicalEnrichedTurn } from './fixtures';
import type { EnrichedTurn } from './schema';

function agentSays(text: string, timestamp: number): EnrichedTurn {
  return canonicalEnrichedTurn({
    role: 'agent',
    content: [{ type: 'text', text }],
    timestamp,
    interrupted: false,
  });
}

function memberSays(text: string, timestamp: number): EnrichedTurn {
  return canonicalEnrichedTurn({
    role: 'user',
    content: [{ type: 'text', text }],
    timestamp,
    source_medium: 'audio',
  });
}

/** A few pleasantries and a hang-up — summarized honestly as non-substantive. */
export const SHORT_TRIVIAL_TRANSCRIPT: EnrichedTurn[] = [
  agentSays('Hi, really glad you called. What has been going on for you?', 1_767_225_600),
  memberSays('Oh — sorry, I think I called by accident. Gotta go.', 1_767_225_608),
];

/** A longer session ranging over sleep, work stress, and a plan adjustment. */
export const LONG_MULTI_TOPIC_TRANSCRIPT: EnrichedTurn[] = [
  agentSays('Welcome back. How did the week treat you?', 1_767_225_600),
  memberSays('Honestly rough. I have not slept properly since Monday.', 1_767_225_615),
  agentSays('That sounds draining. What do you think is keeping you up?', 1_767_225_630),
  memberSays(
    'Work mostly. The project deadline moved up and my manager keeps adding scope.',
    1_767_225_650,
  ),
  agentSays('Would it help to look at the evening wind-down we sketched last time?', 1_767_225_670),
  memberSays(
    'Yes — although I want to change the plan. Reading in bed is not working, I would rather take a walk after dinner.',
    1_767_225_700,
  ),
  agentSays('A walk after dinner it is. I have updated that in your plan.', 1_767_225_720),
  memberSays('Thanks. My mood is actually better than last month, just tired.', 1_767_225_740),
];

/** Acute-distress content — the crisis flag must persist. Wholly synthetic. */
export const CRISIS_TRANSCRIPT: EnrichedTurn[] = [
  agentSays('You mentioned things feel heavy today. I am listening.', 1_767_225_600),
  memberSays(
    'It is worse than heavy. Lately I keep thinking everyone would be better off without me.',
    1_767_225_620,
  ),
  agentSays(
    'Thank you for telling me. That sounds really painful, and I want to connect you with someone who can help right now.',
    1_767_225_640,
  ),
  memberSays('Okay. Yes. I think I need that.', 1_767_225_660),
];

/** Dialogue interleaved with heavy tool traffic — none of it is speech. */
export const TOOL_HEAVY_TRANSCRIPT: EnrichedTurn[] = [
  agentSays('Let me pull up your check-in streak.', 1_767_225_600),
  canonicalEnrichedTurn({
    role: 'agent',
    content: [
      { type: 'tool_use', id: 'toolu_10', name: 'show_progress_chart', input: { range: 'week' } },
      { type: 'tool_result', tool_use_id: 'toolu_10', content: '{"streak":6}', is_error: false },
      { type: 'text', text: 'Six days in a row — that is your best run yet.' },
    ],
    timestamp: 1_767_225_610,
    interrupted: false,
  }),
  memberSays('That actually feels great to see.', 1_767_225_625),
  canonicalEnrichedTurn({
    role: 'agent',
    content: [
      { type: 'tool_use', id: 'toolu_11', name: 'log_insight', input: { kind: 'celebration' } },
    ],
    timestamp: 1_767_225_630,
    interrupted: false,
  }),
  memberSays('Can we set the same target for next week?', 1_767_225_645),
];

/** Concrete member-voiced commitments late in the call — citation material. */
export const COMMITMENT_RICH_TRANSCRIPT: EnrichedTurn[] = [
  agentSays('Last time you wanted to rethink weekends. Where did you land?', 1_767_225_600),
  memberSays('I thought about it a lot. Saturdays are the hard part.', 1_767_225_620),
  agentSays('What would a good Saturday look like?', 1_767_225_640),
  memberSays(
    'I am going to book the climbing gym for Saturday mornings so the day starts differently.',
    1_767_225_660,
  ),
  agentSays('That is a strong anchor. Anything for the evening?', 1_767_225_680),
  memberSays(
    'Yes — I will text my sister before eight if I feel the pull to go out.',
    1_767_225_700,
  ),
];

/**
 * Attribution is a property of the conversation ROW, not the transcript: an
 * unattributed capture carries ordinary dialogue with a null member binding.
 * The e2e slice pairs this transcript with `memberId: null`.
 */
export const UNATTRIBUTED_TRANSCRIPT: EnrichedTurn[] = [
  agentSays('Hi there — before we start, I could not match this call to a profile.', 1_767_225_600),
  memberSays('Strange. Anyway, I wanted to talk about my week.', 1_767_225_615),
];

/**
 * The agent introduces itself as Sona; the member thanks it as "coach" and
 * separately mentions a human coach. The member-facing summary must call the
 * agent Sona and use no role word for anyone, naming the human or saying
 * "your care team" instead.
 */
export const SONA_NAMED_TRANSCRIPT: EnrichedTurn[] = [
  agentSays('Hi, I am Sona. What would you like to talk about today?', 1_767_225_600),
  memberSays(
    'My coach Dana at Pelago told me to try a short walk after work, so I wanted to check in about that.',
    1_767_225_620,
  ),
  agentSays('How have the walks gone so far?', 1_767_225_640),
  memberSays('I did two this week. The evenings felt calmer on those days.', 1_767_225_660),
  agentSays(
    'Two walks and calmer evenings. What would you like to aim for next week?',
    1_767_225_680,
  ),
  memberSays('I will walk three evenings next week. Thanks, coach, this helped.', 1_767_225_700),
];
